import { capitalize, markRaw } from 'vue';

import PacingStrategy from '@/common/enums/pacing-strategy.enum';
import {
  IconColoredPacingStrategyAhead,
  IconColoredPacingStrategyAsap,
  IconColoredPacingStrategyEven,
  IconColoredPacingStrategyOptimized2,
} from '@/common/icons/colored';
import { capitalizeTranslation } from '@/globals';
import i18n from '@/vueI18n';

export interface IPacingStrategyOption {
  id: PacingStrategy;
  name: string;
  description: string;
  icon: any;
  deprecated?: boolean;
  strengthFactors?: IPacingStrategyStrengthFactor[];
  pacingCurvePreview?: (value?: IPacingStrategyStrengthFactor) => { x: number[]; y: number[] };
}

export interface IPacingStrategyStrengthFactor {
  strengthFactor: number;
  description: string;
  id: string;
  multiplier: number;
  name: string;
}

export default class PacingStrategyOptions {
  static options(): IPacingStrategyOption[] {
    const strengthFactorLabel = i18n.global.t('names.strengthFactor');
    const sfInitials = strengthFactorLabel
      .split(' ')
      .map((word) => capitalize(word.charAt(0)))
      .join('');
    const aheadName = (strengthFactor: number) => {
      return `${capitalize(i18n.global.t('messages.pacingStrategyAhead'))} (${sfInitials}${strengthFactor})`;
    };

    return [
      {
        id: PacingStrategy.AUDIENCE,
        name: capitalize(i18n.global.t('messages.pacingStrategyOptimized')),
        description: capitalize(i18n.global.t('messages.pacingStrategyOptimizedDescription')),
        icon: markRaw(IconColoredPacingStrategyOptimized2),
      },
      {
        id: PacingStrategy.LINEAR,
        name: capitalize(i18n.global.t('messages.pacingStrategyEven')),
        description: capitalize(i18n.global.t('messages.pacingStrategyEvenDescription')),
        icon: markRaw(IconColoredPacingStrategyEven),
      },
      {
        id: PacingStrategy.SQRT,
        name: `${capitalize(i18n.global.t('messages.pacingStrategyAhead'))}`,
        description: capitalize(i18n.global.t('messages.pacingStrategyAheadDescription')),
        icon: markRaw(IconColoredPacingStrategyAhead),
        deprecated: true, // This strategy is deprecated. It's now the same thing as AHEAD with a strength factor of 5. We keep it here for backwards compatibility.
      },
      {
        id: PacingStrategy.AHEAD,
        name: capitalize(i18n.global.t('messages.pacingStrategyAhead')),
        description: capitalize(i18n.global.t('messages.pacingStrategyAheadDescription')),
        icon: markRaw(IconColoredPacingStrategyAhead),
        strengthFactors: [
          {
            strengthFactor: 1,
            description: capitalizeTranslation('messages.lessAggressive'),
            id: 'ahead_1',
            multiplier: 0.9,
            name: aheadName(1),
          },
          {
            strengthFactor: 2,
            description: null,
            id: 'ahead_2',
            multiplier: 0.8,
            name: aheadName(2),
          },
          {
            strengthFactor: 3,
            description: capitalizeTranslation('messages.moderate'),
            id: 'ahead_3',
            multiplier: 0.7,
            name: aheadName(3),
          },
          {
            strengthFactor: 4,
            description: null,
            id: 'ahead_4',
            multiplier: 0.6,
            name: aheadName(4),
          },
          {
            strengthFactor: 5,
            description: capitalizeTranslation('messages.mostAggressive'),
            id: 'ahead_5',
            multiplier: 0.5,
            name: aheadName(5),
          },
        ],
        pacingCurvePreview: (value: IPacingStrategyStrengthFactor) => {
          // formula is progress ** multiplier
          const x = [];
          for (let i = 0; i <= 1; i += 0.1) {
            x.push(i);
          }
          const y = [...x].map((i) => i ** value.multiplier);

          return { x, y };
        },
      },
      {
        id: PacingStrategy.FASTEST,
        name: capitalize(i18n.global.t('messages.pacingStrategyAsap')),
        description: capitalize(i18n.global.t('messages.pacingStrategyAsapDescription')),
        icon: markRaw(IconColoredPacingStrategyAsap),
      },
    ];
  }

  getStrategies(deliveryCurve: string) {
    const availableStrategies = PacingStrategyOptions.options().filter((strategy) => !strategy.deprecated);
    const deprecatedStrategies = PacingStrategyOptions.options().filter((strategy) => strategy.deprecated);
    const isDeliveryCurveDeprecated = deprecatedStrategies.find((strategy) => strategy.id === deliveryCurve);

    if (isDeliveryCurveDeprecated) {
      availableStrategies.push(isDeliveryCurveDeprecated);
    }

    return availableStrategies;
  }

  findStrategy(deliveryCurve: string): IPacingStrategyOption {
    const availableStrategies = this.getStrategies(deliveryCurve);
    return availableStrategies.find((strategy) => {
      const strengthFactor = strategy.strengthFactors?.find((strengthFactor) => strengthFactor.id === deliveryCurve);

      return strengthFactor ?? strategy.id === deliveryCurve;
    });
  }

  getStrategiesForInlineEditing() {
    const availableStrategies = PacingStrategyOptions.options().filter((strategy) => !strategy.deprecated);

    return availableStrategies.flatMap((strategy) => {
      if (strategy.strengthFactors) {
        return strategy.strengthFactors.map((strengthFactor) => ({
          id: strengthFactor.id,
          name: strengthFactor.name,
        }));
      }

      return {
        id: strategy.id,
        name: strategy.name,
      };
    });
  }
}
